/**
 * Column policy and localStorage cache-key resolution for the Inspection Series listing.
 *
 * Rules from the contract and task-01 inventory:
 *  - allowed: every key that may appear in visibleColumnKeys for this scope.
 *  - defaults: keys visible in the default set (derived from DEFAULT_HIDDEN_COLUMNS helpers).
 *  - required: selectInspectionSeries, id, and actions — always visible, cannot be hidden.
 *  - Cache keys must stay compatible with the legacy Kendo shapes.
 *
 * Default hidden columns (from Ruby helpers):
 *  - Standalone (InspectionSeriesHelper): referenceNumber, contractNumber, account, autoExtend
 *  - Building   (BuildingsHelper):        building, referenceNumber, contractNumber, account, autoExtend
 *  - Account    (AccountsHelper):         referenceNumber, contractNumber, account, autoExtend
 *
 * Cache keys (from Ruby SERIES_CACHE_KEY constants):
 *  - Standalone: kendo/inspection_series/index
 *  - Building:   kendo/buildings/inspection_series
 *  - Account:    kendo/accounts/inspection_series
 *
 * Note: `selectInspectionSeries` (the selector pseudo-column) is excluded from
 * ALL_SERIES_COLUMNS because it is structural (always present, not user-configurable).
 * It is listed in `required` so the sanitizer never removes it from storage if it
 * appears there due to legacy serialization.
 */
// ---------------------------------------------------------------------------
// All known column keys (display order — selectInspectionSeries handled separately)
// ---------------------------------------------------------------------------
/**
 * All user-facing column keys in display order.
 * `selectInspectionSeries` is a structural pseudo-column and is NOT in this list.
 * `actions` is the row-actions column; required and always present.
 */
export const ALL_SERIES_COLUMNS = [
    'id',
    'referenceNumber',
    'contractNumber',
    'building',
    'account',
    'frequencies',
    'expirationDate',
    'systemAsset',
    'inspectionsCount',
    'status',
    'autoExtend',
    'actions'
];
/**
 * Columns that are always visible and cannot be hidden by the user.
 * Mirrors NON_HIDABLE_COLUMNS from InspectionSeriesHelper / BuildingsHelper /
 * AccountsHelper (selectInspectionSeries, id, actions), minus the structural
 * pseudo-column which is not managed in visibleColumnKeys.
 */
const REQUIRED_COLUMNS = ['id', 'actions'];
/**
 * Computed / action columns have no sortable DB field. Listing headers must not
 * send these as sort[0][field] (IP-15590 — frequencies/systemAsset/inspectionsCount 500).
 */
export const SERIES_NON_SORTABLE_KEYS = ['actions', 'frequencies', 'systemAsset', 'inspectionsCount'];
// ---------------------------------------------------------------------------
// Column policy factory
// ---------------------------------------------------------------------------
/**
 * Returns the column policy for a given scope.
 *
 * - `standalone`: all columns except [referenceNumber, contractNumber, account, autoExtend] visible by default.
 * - `building`:   all columns except [building, referenceNumber, contractNumber, account, autoExtend] visible by default.
 * - `account`:    all columns except [referenceNumber, contractNumber, account, autoExtend] visible by default.
 *
 * Series has no IIM-gated column — allowed is always the full set.
 */
export function columnPolicyForScope(scope) {
    const allowed = [...ALL_SERIES_COLUMNS];
    const defaultHidden = scope.type === 'building'
        ? ['building', 'referenceNumber', 'contractNumber', 'account', 'autoExtend']
        : ['referenceNumber', 'contractNumber', 'account', 'autoExtend'];
    const defaults = allowed.filter((c) => !defaultHidden.includes(c));
    return {
        allowed,
        defaults,
        required: [...REQUIRED_COLUMNS]
    };
}
// ---------------------------------------------------------------------------
// Cache key factory
// ---------------------------------------------------------------------------
/**
 * Returns the localStorage cache key for a given scope.
 * Matches the legacy Kendo keys set by Ruby views/helpers.
 *
 * standalone: kendo/inspection_series/index        (InspectionSeriesHelper)
 * building:   kendo/buildings/inspection_series    (BuildingsHelper::SERIES_CACHE_KEY)
 * account:    kendo/accounts/inspection_series     (AccountsHelper::SERIES_CACHE_KEY)
 *
 * Note: The `kendo/` prefix is already included here.  The legacy ERB mounts
 * supply a `cacheKey` prop WITHOUT the prefix (e.g. "inspection_series/index"),
 * but the React component prepends "kendo/".  We produce the full prefixed key
 * directly so the preference hook can call localStorage directly without an
 * extra prefix step.
 */
export function cacheKeyForScope(scope) {
    switch (scope.type) {
        case 'building':
            return 'kendo/buildings/inspection_series';
        case 'account':
            return 'kendo/accounts/inspection_series';
        case 'standalone':
        default:
            return 'kendo/inspection_series/index';
    }
}
